import { Button } from "../../components/ui/button";
import { fmtRange } from "../../lib/dates";
import {
  ArrowLeft,
  Sparkles,
  Loader2,
  Check,
  FlaskConical,
  Wand2,
  History as HistoryIcon,
} from "lucide-react";

/**
 * Top header bar for the Roster Detail page.
 * Pure presentational — receives the roster + callbacks from the parent
 * (RosterDetail) so all data-fetch / mutation logic stays there.
 */
const RosterHeader = ({
  roster,
  generating,
  publishing,
  onBack,
  onWhatIf,
  onPublish,
  onGenerate,
  onRegenerateKeepLocks,
  onOpenHistory,
}) => {
  if (!roster) return null;
  const hasEntries = (roster.entries?.length || 0) > 0;
  const isPublished = roster.status === "published";

  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      data-testid="roster-header"
    >
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-2xl tracking-tight font-bold text-zinc-900 dark:text-zinc-100">
            {roster.name}
          </h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
            {fmtRange(roster.start_date, roster.end_date)}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <span
          className={`text-xs font-medium px-3 py-1.5 rounded capitalize ${
            isPublished
              ? "bg-emerald-50 text-emerald-700"
              : "bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400"
          }`}
          data-testid="roster-status-badge"
        >
          {roster.status}
        </span>

        {!isPublished && hasEntries && (
          <Button variant="outline" onClick={onWhatIf} data-testid="what-if-btn">
            <FlaskConical className="w-4 h-4 mr-2" />
            What If…
          </Button>
        )}

        {!isPublished && hasEntries && (
          <Button
            variant="outline"
            onClick={onPublish}
            disabled={publishing}
            data-testid="publish-roster-btn"
          >
            {publishing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Check className="w-4 h-4 mr-2" />
            )}
            Publish
          </Button>
        )}

        <Button
          onClick={() => onGenerate(false)}
          disabled={generating}
          className="bg-[#002FA7] hover:bg-[#00227A] text-white relative overflow-hidden"
          data-testid="generate-roster-btn"
        >
          {generating ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              {hasEntries ? "Regenerate" : "Generate with AI"}
            </>
          )}
        </Button>

        {!isPublished && hasEntries && (
          <Button
            variant="outline"
            onClick={onRegenerateKeepLocks}
            disabled={generating}
            title="Re-runs the engine but keeps any shifts you manually edited (locked)."
            data-testid="regenerate-keep-locks-btn"
          >
            <Wand2 className="w-4 h-4 mr-2" />
            Regenerate (keep edits)
          </Button>
        )}

        {!isPublished && (
          <Button
            variant="outline"
            onClick={onOpenHistory}
            title="Revert to a previous generation"
            data-testid="open-history-btn"
          >
            <HistoryIcon className="w-4 h-4 mr-2" />
            Revert
          </Button>
        )}
      </div>
    </div>
  );
};

export default RosterHeader;
